import { type AbstractMesh, MeshBuilder, type Scene, type TransformNode, Vector3, VertexBuffer } from "@babylonjs/core";
import type { Bounds, ColliderMode } from "./colliderRules";
import { bodyCaught } from "./body";
import { type Overlap, overlapsBody } from "./trapGuard";

export interface ColliderResult {
  /** Meshes whose checkCollisions the trap guard may toggle: the geometry in mesh mode, the box in box mode. */
  collidable: AbstractMesh[];
  /** World-space AABB of the collidable meshes; null when the entity is passable. */
  bounds: Bounds | null;
  /** The trap guard's test for whether the player is caught in this entity; absent when it is passable. */
  overlaps?: Overlap;
}

/** Triangle count of an entity: indexed meshes by index count, unindexed by vertex count (spec 2k §2.2). */
export function entityTriangles(meshes: AbstractMesh[]): number {
  let n = 0;
  for (const m of meshes) {
    const indices = m.getTotalIndices();
    n += Math.floor((indices > 0 ? indices : m.getTotalVertices()) / 3);
  }
  return n;
}

/** Apply the resolved mode: flag the geometry, or build one invisible box in the entity's local frame. */
export function applyCollider(scene: Scene, root: TransformNode, meshes: AbstractMesh[], mode: ColliderMode, entityId: string): ColliderResult {
  for (const m of meshes) m.checkCollisions = false;
  // GLBs arrive with an empty __root__ node whose bounding box is a point at the origin: it must not shape anything.
  const geometry = meshes.filter((m) => m.getTotalVertices() > 0);
  if (mode === "none" || geometry.length === 0) return { collidable: [], bounds: null };
  root.computeWorldMatrix(true);
  const corners = worldCorners(geometry);
  if (mode === "mesh") {
    for (const m of geometry) m.checkCollisions = true;
    const bounds = aabb(corners)!;
    // inside the bounds of a hollow shape (a house) the player is free; cutting into its surface or standing in
    // solid material traps them
    let tris: Float32Array | null = null;
    const overlaps = (feet: { x: number; y: number; z: number }) =>
      overlapsBody(feet, bounds) && bodyCaught(feet, (tris ??= worldTriangles(geometry)));
    return { collidable: geometry, bounds, overlaps };
  }
  const inverse = root.getWorldMatrix().clone().invert();
  const local = aabb(corners.map((p) => Vector3.TransformCoordinates(p, inverse)))!;
  const size = new Vector3(local.max.x - local.min.x, local.max.y - local.min.y, local.max.z - local.min.z);
  const box = MeshBuilder.CreateBox(`entity:${entityId}:collider`, { width: Math.max(size.x, 0.01), height: Math.max(size.y, 0.01), depth: Math.max(size.z, 0.01) }, scene);
  box.parent = root;
  box.position.set((local.min.x + local.max.x) / 2, (local.min.y + local.max.y) / 2, (local.min.z + local.max.z) / 2);
  box.isVisible = false;
  box.isPickable = false;
  box.checkCollisions = true;
  box.metadata = { entityId };
  box.computeWorldMatrix(true);
  const bounds = aabb(worldCorners([box]))!;
  return { collidable: [box], bounds, overlaps: (feet) => overlapsBody(feet, bounds) };
}

/** Every triangle of the meshes in world space, 9 floats each; the entity never moves once placed. */
function worldTriangles(meshes: AbstractMesh[]): Float32Array {
  const out: number[] = [];
  const v = new Vector3();
  for (const m of meshes) {
    const pos = m.getVerticesData(VertexBuffer.PositionKind);
    if (!pos) continue;
    const world = m.computeWorldMatrix(true);
    const idx = m.getIndices() ?? Array.from({ length: pos.length / 3 }, (_, i) => i);
    for (const i of idx) {
      Vector3.TransformCoordinatesFromFloatsToRef(pos[i * 3]!, pos[i * 3 + 1]!, pos[i * 3 + 2]!, world, v);
      out.push(v.x, v.y, v.z);
    }
  }
  return new Float32Array(out);
}

function worldCorners(meshes: AbstractMesh[]): Vector3[] {
  const out: Vector3[] = [];
  for (const m of meshes) {
    m.computeWorldMatrix(true);
    out.push(...m.getBoundingInfo().boundingBox.vectorsWorld);
  }
  return out;
}

function aabb(points: Vector3[]): Bounds | null {
  if (points.length === 0) return null;
  const min = { x: Infinity, y: Infinity, z: Infinity }, max = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (const p of points) {
    min.x = Math.min(min.x, p.x); min.y = Math.min(min.y, p.y); min.z = Math.min(min.z, p.z);
    max.x = Math.max(max.x, p.x); max.y = Math.max(max.y, p.y); max.z = Math.max(max.z, p.z);
  }
  return { min, max };
}
